import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath } from 'react-router';

import { zIndices } from '~/eds/src/tokens/zIndices';
import { useDocumentGroups } from '~/redux/api/methods/group-nodes/useDocumentGroups';
import chatbot from '~/redux/slices/chatbot';
import { useRouting } from '~/routing/useRouting';
import { Entity, EntityType } from '~/types/Entity';

import { EntityContext } from '../types/chatbot';
import { selectDocument, selectFolderTree } from './selectors';
import {
  DEFAULT_DOCUMENTS_ID,
  DEFAULT_SEARCH_ID,
  getDefaultChatContext,
  getFolderContext,
  getGroupQuery,
  pathMatch,
  ROUTES,
} from './utils';

const resolveRoute = (pathname: string) => {
  const routePath = Object.values(ROUTES).find((route) => {
    return matchPath(route, pathname);
  });
  return routePath ?? null;
};

interface UseResolveChatBotEntityProps {
  entity: Entity<EntityType>;
  context: EntityContext | undefined;
  isEntityLoading?: boolean;
}

export const useResolveChatBotEntity = (): UseResolveChatBotEntityProps => {
  const dispatch = useDispatch();
  const { location, params } = useRouting();
  const resolvedRoute = resolveRoute(location.pathname);

  // redux store selectors
  const document = useSelector(selectDocument);
  const folderTree = useSelector(selectFolderTree);

  const { data: documentGroupsData } = useDocumentGroups(document?.id);

  const intl = useIntl();

  const panelZIndex =
    resolvedRoute === ROUTES.xRay && params?.modelId && params.modelId !== 'new'
      ? zIndices.floatingPanel
      : zIndices.panel;

  useEffect(() => {
    dispatch(chatbot.actions.setPanelZIndex(panelZIndex));
  }, [dispatch, panelZIndex]);

  // Memoized so callers (useAskAnything's effects) get a stable entity/context
  // reference across renders where none of these inputs actually changed.
  return useMemo<UseResolveChatBotEntityProps>(() => {
    let isEntityLoading = false;
    const context: EntityContext = [getDefaultChatContext(intl)];

    const preSigEntity = {
      type: 'ticket' as const,
      id: pathMatch(location.pathname, ROUTES.ticketViewPage)!,
    };

    switch (resolvedRoute) {
      case ROUTES.ticketViewPage:
      case ROUTES.reviewerPage:
        if (document) {
          return {
            entity: preSigEntity,
            context: [
              {
                query: [],
                type: 'document_version',
                label: intl.formatMessage({
                  id: 'UseResolveChatBotEntity.thisDocumentVersionLabel',
                  defaultMessage: 'This Document Version',
                  description: 'Label for current document version context',
                }),
                title: document.name,
                icon: document.type === '.pdf' ? 'file-pdf' : 'file-doc',
                primary: true,
                searchEntity: 'document',
                selectedIds: [document.id.toString()],
                id: document.id.toString(),
              },
            ],
          };
        }
        isEntityLoading = true;
        break;
      case ROUTES.folder: {
        const folderId = Number(pathMatch(location.pathname, ROUTES.folder));
        return {
          entity: { type: 'all_documents', id: folderId.toString() },
          // Defining as undefined to allow override in folder's page.
          context: undefined,
        };
      }
      case ROUTES.document:
        if (document) {
          const folderContext = getFolderContext(
            document.folder,
            folderTree,
            undefined,
            intl,
          );
          if (folderContext) context.push(folderContext);
          const documentGroups = documentGroupsData?.included ?? [];
          const groupIds = documentGroups.map((g) => g.id);
          if (groupIds.length > 0) {
            context.push({
              label: intl.formatMessage(
                {
                  id: 'UseResolveChatBotEntity.groupsLabel',
                  defaultMessage:
                    '{count, plural, one {This Group} other {These Groups}}',
                  description:
                    'Label for group context on a document. count is the number of groups.',
                },
                { count: groupIds.length },
              ),
              query: getGroupQuery(groupIds),
              title: documentGroups.map((g) => g.attributes.name).join(', '),
              icon: 'groups',
              searchEntity: 'document',
              selectedIds: [],
              id: groupIds[0],
              type: 'document_group',
            });
          }
          context.push({
            type: 'document',
            label: intl.formatMessage({
              id: 'UseResolveChatBotEntity.thisDocumentLabel',
              defaultMessage: 'This Document',
              description: 'Label for current document context',
            }),
            title: document.name,
            icon: document.type === '.pdf' ? 'file-pdf' : 'file-doc',
            primary: true,
            searchEntity: 'document',
            query: [],
            selectedIds: [document.id.toString()],
            searchEntityContext: [
              { id: `${document.handlerId}`, type: 'document_handler' },
            ],
            id: document.id.toString(),
          });

          return {
            entity: {
              type: 'document_version',
              id: String(document.versionId),
            },
            context,
          };
        }
        break;
      case ROUTES.dashboard:
        return {
          entity: {
            type: 'all_documents',
            id: pathMatch(location.pathname, ROUTES.dashboard)!,
          },
          context: undefined,
        };
      case ROUTES.search:
      case ROUTES.savedSearch:
        return {
          entity: {
            type: 'all_documents',
            id:
              pathMatch(location.pathname, ROUTES.savedSearch) ??
              DEFAULT_SEARCH_ID,
          },
          context: undefined,
        };
      case ROUTES.documentGroupDetail:
        return {
          entity: {
            type: 'all_documents',
            id: pathMatch(location.pathname, ROUTES.documentGroupDetail)!,
          },
          context: undefined,
        };
      case ROUTES.workflowGroup:
        return {
          entity: {
            type: 'all_documents',
            id: pathMatch(location.pathname, ROUTES.workflowGroup)!,
          },
          context: undefined,
        };
      case ROUTES.xRay: {
        const { modelId } = params;
        return {
          entity: {
            type: 'model',
            id: modelId!,
          },
          context: modelId
            ? [
                {
                  label: intl.formatMessage({
                    id: 'UseResolveChatBotEntity.thisModelLabel',
                    defaultMessage: 'This Model',
                    description: 'Label for current model context',
                  }),
                  title: 'This Model',
                  icon: 'field',
                  searchEntity: 'model',
                  query: [],
                  primary: true,
                  selectedIds: [modelId],
                  searchEntityContext: [{ id: modelId, type: 'model' }],
                  id: modelId,
                  type: 'model',
                },
              ]
            : undefined,
        };
      }
      default:
        break;
    }

    return {
      entity: {
        type: 'all_documents',
        id: DEFAULT_DOCUMENTS_ID,
      },
      context: [
        {
          ...getDefaultChatContext(intl),
          primary: true,
        },
      ],
      isEntityLoading,
    };
  }, [
    resolvedRoute,
    document,
    folderTree,
    documentGroupsData,
    intl,
    location.pathname,
    params?.modelId,
  ]);
};
